import { formatPrice } from '@folio/lib';
import { isPerilValueIncluded } from './peril-value';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { useHasPolicyProductConfig } from '@folio/utils/usePolicyProductConfig';
import { usePolicyProductConfig } from '@folio/utils/usePolicyProductConfig';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import {
  assetControllerGetAssetExternalQuoteQueryOptions,
  configControllerGetPublicProductConfigQueryOptions,
  type Asset,
  type KeyValueFieldObject,
} from 'korint-sdk';
import { PlusIcon } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Group } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

export const QuoteTableOptionalPerils = ({
  asset,
  branchId,
}: {
  asset: Asset<KeyValueFieldObject>;
  branchId?: string;
}) => {
  const { t } = useTranslation();

  const quoteTierModuleConfig = usePolicyProductConfig('quoteTierModuleConfig');

  const { policy } = usePolicyOnDashboard();
  const isGuaranteeExcessDisplayed = useHasPolicyProductConfig('isGuaranteeExcessDisplayed');
  const { data: config } = useSuspenseQuery({
    ...configControllerGetPublicProductConfigQueryOptions(policy.productId),
  });

  const chosenTier = useMemo(() => asset?.quote?.options.tier, [asset]);

  const availablePerilsConfig = useMemo(
    () => config?.quote?.availableConfigs.find(item => item.tier === chosenTier)?.perils,
    [config, chosenTier],
  );

  const perilCustomFieldsMap = useMemo(
    () =>
      Object.fromEntries(
        (config?.quote?.availablePerils ?? []).flatMap(p =>
          p.customFields?.length ? [[p.id, p.customFields[0]]] : [],
        ),
      ),
    [config?.quote?.availablePerils],
  );

  const optionalPerilsInTiers = useMemo(
    () =>
      asset?.quote?.options?.perils?.filter(
        peril => !availablePerilsConfig!.find(element => element.id === peril.id)?.required,
      ) ?? [],
    [asset, availablePerilsConfig],
  );

  const { data: wakamQuote } = useQuery({
    ...assetControllerGetAssetExternalQuoteQueryOptions(asset.id, { branchId }),
    enabled: quoteTierModuleConfig?.isExternalQuoteEnabled,
  });

  if (optionalPerilsInTiers.length === 0) {
    return null;
  }

  return optionalPerilsInTiers.map((peril, index) => {
    let excess: string | undefined;
    const wakamData = wakamQuote?.data as {
      Garanties?: { CodeGarantie: string; Franchise: string }[];
    };
    if (wakamData?.Garanties) {
      excess = wakamData.Garanties.find((garantie: { CodeGarantie: string }) =>
        quoteTierModuleConfig.perilsMapping[peril.id]?.includes(garantie.CodeGarantie),
      )?.Franchise as string | undefined;
    }

    // No external quote for this product: the excess is declared per peril in the translations.
    if (isGuaranteeExcessDisplayed) {
      excess ??= t(`${policy?.productId}.perils.${peril.id}.excess`);
    }

    const isUnavailable = Boolean(peril.unavailabilityMessage);

    const configPeril = availablePerilsConfig?.find(p => p.id === peril.id);
    const customFieldName =
      quoteTierModuleConfig?.perilCustomFieldsMapping?.[peril.id] ?? perilCustomFieldsMap[peril.id];
    const cfRaw = customFieldName
      ? (policy?.customFields as Record<string, unknown>)?.[customFieldName]
      : undefined;
    const rawValue = (peril.option ??
      (cfRaw !== undefined ? String(cfRaw) : undefined) ??
      configPeril?.options?.[0]) as string | undefined;
    if (rawValue !== undefined && !isPerilValueIncluded(rawValue)) return null;
    const value = rawValue
      ? t([
          `${policy?.productId}.perils.${peril.id}.options.${rawValue}`,
          `options.${rawValue}`,
          rawValue,
        ])
      : undefined;

    return (
      <tr key={peril.id} className="text-sm hover:bg-gray-100">
        <td className="pr-12 font-semibold bg-white hidden md:table-cell">
          {!index && t('quoteTierCompareModule.optionalPerils')}
        </td>
        <td className="p-1">
          <Group gap={0} className={isUnavailable ? 'text-gray-400' : undefined}>
            {isUnavailable ? (
              <div className="mr-3 h-4 w-4" />
            ) : (
              <PlusIcon className="h-4 w-4 mr-3 shrink-0" />
            )}
            {t(`${policy?.productId}.perils.${peril.id}.label`, {
              value,
              count: Number(rawValue) || 0,
            })}
            {peril.unavailabilityMessage && (
              <Text inline size="xs" className="ml-2 text-red-600">
                {peril.unavailabilityMessage}
              </Text>
            )}
          </Group>
        </td>
        <td className="p-1">
          <Group gap={0}>
            {Number.isNaN(Number(excess)) ? excess : formatPrice(Number(excess), 1)}
          </Group>
        </td>
      </tr>
    );
  });
};
